import { z } from 'zod';
import { PointPayMerchantOriginSchema, PointPaySaleIdSchema, } from '../types/sale/sale-links';
import { VerifiedBillingSetupSchema } from './billing-schedule';
import { PublicExperienceContactInputSchema } from './contact';
import { ProviderExecutionScopeSchema } from './execution-scope';
import { BusinessIdSchema, CheckoutIdSchema, ExperienceIdSchema, FirestoreDocIdSchema, IsoTimestampSchema, PublicExperiencePublicIdSchema, PublishedRevisionSchema, Sha256HexSchema, StripeInvoiceIdSchema, SubmissionIdSchema, } from './primitives';
/** Every authority deadline must also be a finite JavaScript instant. */
export const JotformTimestampSchema = IsoTimestampSchema.refine((value) => Number.isFinite(Date.parse(value)), 'Expected a finite timestamp');
export const JOTFORM_ATTEMPT_PROOF_TTL_SECONDS = 86400;
export const JOTFORM_HANDOFF_TTL_SECONDS = 120;
export const JOTFORM_API_HOSTS = {
    US: 'api.jotform.com',
    EU: 'eu-api.jotform.com',
};
export const JotformIntegrationIdSchema = z
    .string()
    .regex(/^jfi_[a-z0-9]{20}$/);
export const JotformConnectionIdSchema = FirestoreDocIdSchema;
export const JotformFormIdSchema = z.string().regex(/^[1-9][0-9]{0,31}$/);
export const JotformSubmissionIdSchema = JotformFormIdSchema;
export const JotformQuestionIdSchema = JotformFormIdSchema;
export const JotformRegionSchema = z.enum(['US', 'EU']);
export const JotformBindingRevisionSchema = z
    .number()
    .int()
    .min(1)
    .max(Number.MAX_SAFE_INTEGER);
export const JotformAttemptProofSchema = z
    .string()
    .regex(/^jfa_[A-Za-z0-9_-]{43}$/);
export const JotformHandoffTokenSchema = z
    .string()
    .regex(/^jfh_[A-Za-z0-9_-]{43}$/);
export const JotformPopupCapabilitySchema = z
    .string()
    .regex(/^jfp_[A-Za-z0-9_-]{43}$/);
export const JotformNonceSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const JotformEnvironmentKeySchema = z
    .string()
    .regex(/^[a-z0-9_-]{1,64}$/);
export const JotformMappedContactInputSchema = PublicExperienceContactInputSchema;
export const JotformContactFieldMappingSchema = z
    .object({ questionId: JotformQuestionIdSchema })
    .strict();
export const JOTFORM_CONTACT_FIELDS = [
    'firstName',
    'lastName',
    'email',
    'phone',
];
export const JotformContactMappingsSchema = z
    .object({
    schemaVersion: z.literal(1),
    layout: z.literal('SEPARATE_FIELDS_V1'),
    firstName: JotformContactFieldMappingSchema,
    lastName: JotformContactFieldMappingSchema,
    email: JotformContactFieldMappingSchema,
    phone: JotformContactFieldMappingSchema,
})
    .strict()
    .refine((value) => new Set(JOTFORM_CONTACT_FIELDS.map((field) => value[field].questionId))
    .size === 4, 'contact mapping question IDs must be distinct');
export const refineJotformWidgetMapping = (value, ctx) => {
    for (const field of JOTFORM_CONTACT_FIELDS) {
        if (value.contactMappings[field].questionId === value.widgetFieldId)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['contactMappings', field, 'questionId'],
                message: 'contact field must differ from widget field',
            });
    }
};
export const JotformDiagnosticCodeSchema = z.enum([
    'CONNECTION_UNAVAILABLE',
    'FORM_NOT_ACCESSIBLE',
    'FORM_UNSUPPORTED',
    'WIDGET_NOT_FOUND',
    'WIDGET_NOT_REQUIRED',
    'MAPPING_INVALID',
    'PAYFORM_UNPUBLISHED',
    'PAYMENT_INELIGIBLE',
    'PAYMENT_DOMAINS_MISSING',
    'SDK_UNVERIFIED',
    'CONTACT_CHANGED',
    'RECEIPT_CONFLICT',
    'VERIFICATION_UNAVAILABLE',
    'PROVIDER_RESULT_UNCERTAIN',
    'RECOVERY_REQUIRED',
    'REDIRECT_UNVERIFIED',
    'REDIRECT_MISMATCH',
]);
export const JotformDiagnosticSchema = z
    .object({
    code: JotformDiagnosticCodeSchema,
    field: z
        .enum([
        'connection',
        'form',
        'widget',
        'firstName',
        'lastName',
        'email',
        'phone',
        'payment',
        'association',
        'redirect',
    ])
        .optional(),
    severity: z.enum(['BLOCKER', 'WARNING']),
    message: z.string().min(1).max(500),
})
    .strict();
export const JotformDiagnosticsSchema = z
    .array(JotformDiagnosticSchema)
    .max(20);
export const JotformProviderAccountIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,128}$/);
export var JotformPaymentTimingEnum;
(function (JotformPaymentTimingEnum) {
    JotformPaymentTimingEnum["AFTER_SUBMISSION"] = "AFTER_SUBMISSION";
    JotformPaymentTimingEnum["BEFORE_SUBMISSION"] = "BEFORE_SUBMISSION";
})(JotformPaymentTimingEnum || (JotformPaymentTimingEnum = {}));
export const JotformPaymentTimingSchema = z.nativeEnum(JotformPaymentTimingEnum);
export var JotformSourceModeEnum;
(function (JotformSourceModeEnum) {
    JotformSourceModeEnum["SUBMISSION_REDIRECT"] = "SUBMISSION_REDIRECT";
    JotformSourceModeEnum["PRE_SUBMISSION_PAYMENT"] = "PRE_SUBMISSION_PAYMENT";
})(JotformSourceModeEnum || (JotformSourceModeEnum = {}));
export const JotformSourceModeSchema = z.nativeEnum(JotformSourceModeEnum);
export const JotformAfterSubmissionTimingSchema = z.literal('AFTER_SUBMISSION');
export const JotformBeforeSubmissionTimingSchema = z.literal('BEFORE_SUBMISSION');
export const JotformTimingConversionSchema = z
    .object({
    integrationId: JotformIntegrationIdSchema,
    fromPaymentTiming: JotformPaymentTimingSchema,
})
    .strict();
export const JotformTimingChangeDecisionSchema = z.discriminatedUnion('state', [
    z
        .object({
        state: z.literal('SAFE_TO_SWITCH'),
        checkedRevision: JotformBindingRevisionSchema,
    })
        .strict(),
    z
        .object({
        state: z.literal('BLOCKED_CREATE_NEW_BINDING'),
        checkedRevision: JotformBindingRevisionSchema,
        reason: z.enum([
            'ENABLED_BINDING',
            'ATTEMPT_HISTORY',
            'HISTORY_UNAVAILABLE',
        ]),
    })
        .strict(),
]);
/** The two entry modes share the current contract; timing is never inferred. */
export function getJotformPaymentTiming(value) {
    return JotformPaymentTimingSchema.parse(value.paymentTiming);
}
export function evaluateJotformTimingChange(binding, hasAttempts) {
    const checkedRevision = JotformBindingRevisionSchema.parse(binding.bindingRevision);
    const reason = binding.enabled
        ? 'ENABLED_BINDING'
        : typeof hasAttempts !== 'boolean'
            ? 'HISTORY_UNAVAILABLE'
            : hasAttempts
                ? 'ATTEMPT_HISTORY'
                : null;
    return reason
        ? { state: 'BLOCKED_CREATE_NEW_BINDING', checkedRevision, reason }
        : { state: 'SAFE_TO_SWITCH', checkedRevision };
}
const afterMode = {
    paymentTiming: z.literal('AFTER_SUBMISSION'),
    sourceMode: z.literal('SUBMISSION_REDIRECT'),
};
const beforeMode = {
    paymentTiming: z.literal('BEFORE_SUBMISSION'),
    sourceMode: z.literal('PRE_SUBMISSION_PAYMENT'),
};
const auditShape = {
    createdAt: JotformTimestampSchema,
    updatedAt: JotformTimestampSchema,
};
export const JotformAfterReadinessSchema = z
    .object({
    state: z.enum(['UNVERIFIED', 'READY', 'BLOCKED', 'UNAVAILABLE']),
    checkedRevision: JotformBindingRevisionSchema.nullable(),
    checkedAt: JotformTimestampSchema.nullable(),
    diagnostics: JotformDiagnosticsSchema,
    redirect: z
        .object({
        state: z.enum([
            'MANUAL_UNTESTED',
            'CONFIGURED',
            'VERIFIED',
            'MISMATCH',
            'UNABLE_TO_VERIFY',
        ]),
        checkedAt: JotformTimestampSchema.nullable(),
        lastVerifiedAt: JotformTimestampSchema.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.checkedAt === null) !== (value.checkedRevision === null) ||
        (value.state === 'READY' &&
            (!value.checkedAt ||
                !value.checkedRevision ||
                value.diagnostics.some((d) => d.severity === 'BLOCKER') ||
                value.redirect.state === 'MISMATCH')))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['state'],
            message: 'readiness requires current checks without blockers',
        });
    if (value.redirect.state === 'VERIFIED' && !value.redirect.lastVerifiedAt)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['redirect'],
            message: 'verified redirect requires actual submission evidence',
        });
});
export const JotformBeforeReadinessSchema = JotformAfterReadinessSchema.innerType()
    .extend({
    widget: z
        .object({
        state: z.enum([
            'UNVERIFIED',
            'SUPPORTED',
            'UNSUPPORTED',
            'UNAVAILABLE',
        ]),
        layout: z.enum(['CLASSIC', 'CARD', 'UNKNOWN']),
        questionId: JotformQuestionIdSchema.nullable(),
        required: z.boolean().nullable(),
        checkedAt: JotformTimestampSchema.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((v, ctx) => {
    const { widget, ...redirectChecks } = v;
    const checked = JotformAfterReadinessSchema.safeParse(redirectChecks);
    if (!checked.success)
        for (const issue of checked.error.issues)
            ctx.addIssue(issue);
    if ((widget.state === 'SUPPORTED' &&
        (widget.layout !== 'CLASSIC' ||
            !widget.questionId ||
            widget.required !== true ||
            !widget.checkedAt)) ||
        (v.state === 'READY' &&
            (widget.state !== 'SUPPORTED' || widget.checkedAt !== v.checkedAt)))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['widget'],
            message: 'READY requires current supported Classic required-widget evidence',
        });
});
export const JotformVerificationAuthoritySchema = z
    .object({
    bindingRevision: JotformBindingRevisionSchema,
    connectionId: JotformConnectionIdSchema,
    authorizationRevision: JotformBindingRevisionSchema,
    credentialRef: z.string().min(1).max(512),
    region: JotformRegionSchema,
    sourceMode: JotformSourceModeSchema,
    providerAccountId: JotformProviderAccountIdSchema,
})
    .strict();
/** One bounded form-setup review/intent, stored on the existing private binding.
 * The public plan contains no credentials or arbitrary provider patches. */
export const JotformFormSetupPlanSchema = z
    .object({
    reviewId: z.string().uuid(),
    expectedRevision: JotformBindingRevisionSchema,
    formId: JotformFormIdSchema,
    paymentTiming: z.enum(['AFTER_SUBMISSION', 'BEFORE_SUBMISSION']),
    expiresAt: JotformTimestampSchema,
    redirectUrl: z.string().url().max(2048),
    changes: z.array(z.string().min(1).max(500)).max(10),
    warnings: z.array(z.string().min(1).max(500)).max(20),
    manualActions: z.array(z.string().min(1).max(500)).max(10),
    canApply: z.boolean(),
})
    .strict();
export const JotformFormSetupStateSchema = z.enum([
    'REVIEWED',
    'APPLYING',
    'CONFIGURED',
    'INCOMPLETE',
    'UNCERTAIN',
    'WRITE_PERMISSION_REQUIRED',
    'CONFLICT',
    'UNAVAILABLE',
]);
export const JotformFormSetupJournalSchema = z
    .object({
    plan: JotformFormSetupPlanSchema,
    actorId: FirestoreDocIdSchema,
    authorityFingerprint: Sha256HexSchema,
    providerAccountId: JotformProviderAccountIdSchema,
    region: JotformRegionSchema,
    remoteFingerprint: Sha256HexSchema,
    resultRevision: JotformBindingRevisionSchema,
    state: JotformFormSetupStateSchema,
    redirectState: z.enum(['PENDING', 'DISPATCHED', 'VERIFIED']),
    widgetStep: z
        .object({
        state: z.enum(['PENDING', 'DISPATCHED', 'VERIFIED']),
        // Immutable intended target: null means create, even after its ID is recovered.
        targetQuestionId: JotformQuestionIdSchema.nullable(),
        registrationId: z.string().regex(/^[a-f0-9]{24}$/),
        widgetUrl: z.string().url().max(2048),
        order: z.number().positive().finite(),
        reposition: z.boolean(),
        preservedFingerprint: Sha256HexSchema,
    })
        .strict()
        .optional(),
    widgetQuestionId: JotformQuestionIdSchema.nullable(),
    leaseOwner: z.string().uuid().nullable(),
    leaseExpiresAt: JotformTimestampSchema.nullable(),
    updatedAt: JotformTimestampSchema,
})
    .strict()
    .refine((v) => (v.leaseOwner === null) === (v.leaseExpiresAt === null), 'Setup lease must be complete');
const bindingShape = {
    schemaVersion: z.literal(4),
    integrationId: JotformIntegrationIdSchema,
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    connectionId: JotformConnectionIdSchema,
    region: JotformRegionSchema,
    formId: JotformFormIdSchema,
    contactMappings: JotformContactMappingsSchema,
    bindingRevision: JotformBindingRevisionSchema,
    mappingRevision: JotformBindingRevisionSchema,
    enabled: z.boolean(),
    verificationAuthority: JotformVerificationAuthoritySchema.nullable(),
    formSetup: JotformFormSetupJournalSchema.optional(),
    ...auditShape,
    updatedBy: FirestoreDocIdSchema,
};
const refineBinding = (v, ctx) => {
    if (v.verification.state === 'READY' &&
        (v.verification.checkedRevision !== v.bindingRevision ||
            !v.verificationAuthority ||
            v.verificationAuthority.bindingRevision !== v.bindingRevision ||
            v.verificationAuthority.connectionId !== v.connectionId ||
            v.verificationAuthority.region !== v.region ||
            v.verificationAuthority.sourceMode !== v.sourceMode))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['verification'],
            message: 'Ready requires authority for this exact binding revision',
        });
};
export const JotformAfterBindingDocumentSchema = z
    .object({
    ...bindingShape,
    ...afterMode,
    verification: JotformAfterReadinessSchema,
})
    .strict()
    .superRefine(refineBinding);
export const JotformBeforeBindingDocumentSchema = z
    .object({
    ...bindingShape,
    ...beforeMode,
    widgetFieldId: JotformQuestionIdSchema.nullable(),
    verification: JotformBeforeReadinessSchema,
})
    .strict()
    .superRefine((v, ctx) => {
    refineBinding(v, ctx);
    refineJotformWidgetMapping(v, ctx);
    if (v.widgetFieldId === null &&
        (v.enabled || v.verification.state === 'READY'))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['widgetFieldId'],
            message: 'A pending widget mapping must remain disabled and unready',
        });
    if (v.verification.widget.questionId !== null &&
        v.verification.widget.questionId !== v.widgetFieldId)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['verification', 'widget'],
            message: 'Widget evidence must identify the mapped widget',
        });
});
export const JotformBindingDocumentSchema = z.union([
    JotformAfterBindingDocumentSchema,
    JotformBeforeBindingDocumentSchema,
]);
export const JotformBindingReferenceSchema = z
    .object({
    schemaVersion: z.literal(1),
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    integrationId: JotformIntegrationIdSchema,
})
    .strict();
/** Stable experience-keyed record; every new source and rebind transaction fences this marker. */
export const JotformActiveBindingSchema = JotformBindingReferenceSchema.extend({
    hasAttempts: z.boolean(),
}).strict();
const reservationShape = {
    environmentKey: JotformEnvironmentKeySchema,
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    integrationId: JotformIntegrationIdSchema,
    connectionId: JotformConnectionIdSchema,
    authorizationRevision: JotformBindingRevisionSchema,
    region: JotformRegionSchema,
    formId: JotformFormIdSchema,
    contactMappings: JotformContactMappingsSchema,
    bindingRevision: JotformBindingRevisionSchema,
    mappingRevision: JotformBindingRevisionSchema,
    providerAccountId: JotformProviderAccountIdSchema,
};
export const JotformSaleAnswerContractSchema = z
    .object({
    format: z.enum(['SALE_URL_V2', 'SCHEDULED_SETUP_URL_V1']),
    merchantOrigin: PointPayMerchantOriginSchema,
})
    .strict();
export const JotformInitialSaleSchema = z
    .object({
    saleId: PointPaySaleIdSchema,
    invoiceId: StripeInvoiceIdSchema,
    linkedAt: JotformTimestampSchema,
})
    .strict();
export const JotformCheckoutPresentationProfileSchema = z
    .object({
    kind: z.literal('CHECKOUT_PAYMENT_ELEMENT'),
    apiVersion: z.literal('2025-03-31.basil'),
    uiMode: z.literal('custom'),
})
    .strict();
export const JotformAssociationStateSchema = z.enum([
    'AWAITING_SUBMISSION',
    'VERIFIED',
    'CONFLICT',
    'VERIFICATION_UNAVAILABLE',
]);
export const JotformVerifiedSubmissionSchema = z
    .object({
    submissionId: JotformSubmissionIdSchema,
    verifiedAt: JotformTimestampSchema,
    submittedAt: JotformTimestampSchema.nullable(),
    lastSeenAt: JotformTimestampSchema,
})
    .strict();
export const JotformAfterSourceReservationSchema = z
    .object({
    ...reservationShape,
    ...afterMode,
    jotformSubmissionId: JotformSubmissionIdSchema,
})
    .strict();
export const JotformBeforeSourceReservationSchema = z
    .object({
    ...reservationShape,
    ...beforeMode,
    widgetFieldId: JotformQuestionIdSchema,
    answerContract: JotformSaleAnswerContractSchema,
    contactProvenance: z.literal('BROWSER_OBSERVED'),
    termsHandling: z.literal('JOTFORM_MANAGED'),
})
    .strict()
    .superRefine(refineJotformWidgetMapping);
const sourceShape = {
    schemaVersion: z.literal(4),
    channel: z.literal('JOTFORM'),
    requestId: CheckoutIdSchema,
    contact: JotformMappedContactInputSchema,
    executionScope: ProviderExecutionScopeSchema,
    presentationProfile: JotformCheckoutPresentationProfileSchema,
    offerRevision: PublishedRevisionSchema,
    title: z.string().min(1).max(120),
    businessName: z.string().min(1).max(200),
    cancellationNoticeRendered: z.string().min(1).max(2000),
};
const refineSource = (v, ctx) => {
    if (v.executionScope.chargeModel !== 'DIRECT')
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['executionScope'],
            message: 'Jotform requires a direct connected-account scope',
        });
};
export const JotformAfterCheckoutSourceSchema = z
    .object({ ...sourceShape, ...JotformAfterSourceReservationSchema.shape })
    .strict()
    .superRefine(refineSource);
export const JotformBeforeCheckoutSourceSchema = z
    .object({
    ...sourceShape,
    ...JotformBeforeSourceReservationSchema.innerType().shape,
})
    .strict()
    .superRefine((v, ctx) => {
    refineSource(v, ctx);
    refineJotformWidgetMapping(v, ctx);
});
export const JotformCheckoutSourceSchema = z.union([
    JotformAfterCheckoutSourceSchema,
    JotformBeforeCheckoutSourceSchema,
]);
export const JotformCheckoutAdapterContextSchema = z
    .object({
    source: JotformCheckoutSourceSchema,
    presentationProfile: JotformCheckoutPresentationProfileSchema,
})
    .strict()
    .refine((v) => JSON.stringify(v.source.presentationProfile) ===
    JSON.stringify(v.presentationProfile), 'Presentation must equal the frozen source');
const claimShape = {
    pointcraftSubmissionId: SubmissionIdSchema,
    claimedAt: JotformTimestampSchema,
};
const afterClaim = z.discriminatedUnion('claimed', [
    z.object({ claimed: z.literal(false) }).strict(),
    z
        .object({
        claimed: z.literal(true),
        ...claimShape,
        source: JotformAfterCheckoutSourceSchema,
    })
        .strict(),
]);
const beforeClaim = z.discriminatedUnion('claimed', [
    z.object({ claimed: z.literal(false) }).strict(),
    z
        .object({
        claimed: z.literal(true),
        ...claimShape,
        source: JotformBeforeCheckoutSourceSchema,
    })
        .strict(),
]);
const attemptShape = {
    schemaVersion: z.literal(4),
    requestId: CheckoutIdSchema,
    proofHash: Sha256HexSchema,
    proofExpiresAt: JotformTimestampSchema,
    contact: JotformMappedContactInputSchema,
    executionScope: ProviderExecutionScopeSchema,
    initialSale: JotformInitialSaleSchema.nullable(),
    billingSetup: VerifiedBillingSetupSchema.optional(),
    ...auditShape,
};
const afterAttempt = z
    .object({
    ...attemptShape,
    paymentTiming: z.literal('AFTER_SUBMISSION'),
    reservation: JotformAfterSourceReservationSchema,
    financial: afterClaim,
    state: z.literal('VERIFIED'),
    verifiedSubmission: JotformVerifiedSubmissionSchema,
})
    .strict();
const beforeAttempt = z
    .object({
    ...attemptShape,
    paymentTiming: z.literal('BEFORE_SUBMISSION'),
    reservation: JotformBeforeSourceReservationSchema,
    financial: beforeClaim,
    state: JotformAssociationStateSchema,
    verifiedSubmission: JotformVerifiedSubmissionSchema.nullable(),
})
    .strict();
function refineAttempt(v, ctx) {
    const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const created = Date.parse(v.createdAt), updated = Date.parse(v.updatedAt), expires = Date.parse(v.proofExpiresAt);
    if (updated < created ||
        expires <= created ||
        expires - created > JOTFORM_ATTEMPT_PROOF_TTL_SECONDS * 1000)
        issue('proofExpiresAt', 'Original proof must be bounded by 24 hours');
    if (v.executionScope.chargeModel !== 'DIRECT')
        issue('executionScope', 'Original attempt requires direct scope');
    if (v.financial.claimed) {
        const s = v.financial.source, claimed = Date.parse(v.financial.claimedAt);
        if (s.requestId !== v.requestId ||
            JSON.stringify(s.contact) !== JSON.stringify(v.contact) ||
            JSON.stringify(s.executionScope) !== JSON.stringify(v.executionScope))
            issue('financial', 'Claim must preserve original attempt, contact and account');
        for (const [key, value] of Object.entries(v.reservation))
            if (JSON.stringify(s[key]) !==
                JSON.stringify(value))
                issue('financial', 'Claim must preserve the complete source reservation');
        if (claimed < created || claimed >= expires || claimed > updated)
            issue('financial', 'Claim requires a live original proof');
        if (v.paymentTiming === 'AFTER_SUBMISSION' &&
            claimed < Date.parse(v.verifiedSubmission.verifiedAt))
            issue('financial', 'Claim requires prior saved-submission verification');
    }
    else if (v.initialSale ||
        v.billingSetup ||
        (v.paymentTiming === 'BEFORE_SUBMISSION' &&
            (v.verifiedSubmission || v.state !== 'AWAITING_SUBMISSION')))
        issue('financial', 'Unclaimed attempt cannot carry financial results or before-submit association');
    if (v.initialSale &&
        (!v.financial.claimed ||
            Date.parse(v.initialSale.linkedAt) < Date.parse(v.financial.claimedAt) ||
            Date.parse(v.initialSale.linkedAt) > updated))
        issue('initialSale', 'Initial sale must follow the original claim');
    if (v.billingSetup &&
        (!v.financial.claimed ||
            Date.parse(v.billingSetup.verifiedAt) <
                Date.parse(v.financial.claimedAt) ||
            Date.parse(v.billingSetup.verifiedAt) > updated))
        issue('billingSetup', 'Setup must follow the original claim');
    if (v.paymentTiming === 'AFTER_SUBMISSION') {
        if (v.verifiedSubmission.submissionId !== v.reservation.jotformSubmissionId ||
            Date.parse(v.verifiedSubmission.verifiedAt) < created ||
            Date.parse(v.verifiedSubmission.verifiedAt) > updated)
            issue('verifiedSubmission', 'Verified source must match the original reservation');
    }
    else {
        if (v.billingSetup &&
            v.reservation.answerContract.format !== 'SCHEDULED_SETUP_URL_V1')
            issue('billingSetup', 'Setup requires a scheduled answer contract');
        if ((v.state === 'VERIFIED') !== (v.verifiedSubmission !== null))
            issue('verifiedSubmission', 'Verified state requires its permanent association');
        if (v.verifiedSubmission) {
            const verified = Date.parse(v.verifiedSubmission.verifiedAt), seen = Date.parse(v.verifiedSubmission.lastSeenAt);
            const completed = v.reservation.answerContract.format === 'SCHEDULED_SETUP_URL_V1'
                ? v.billingSetup?.verifiedAt
                : v.initialSale?.linkedAt;
            if (!completed ||
                verified < Date.parse(completed) ||
                verified > updated ||
                seen < verified ||
                seen > updated)
                issue('verifiedSubmission', 'Final association requires original financial completion');
            if (v.verifiedSubmission.submittedAt &&
                (Date.parse(v.verifiedSubmission.submittedAt) < created ||
                    Date.parse(v.verifiedSubmission.submittedAt) > verified))
                issue('verifiedSubmission', 'Final provider submission must belong to this attempt interval');
        }
    }
}
export const JotformAfterAttemptDocumentSchema = afterAttempt.superRefine(refineAttempt);
export const JotformBeforeAttemptDocumentSchema = beforeAttempt.superRefine(refineAttempt);
export const JotformAttemptDocumentSchema = z.union([
    JotformAfterAttemptDocumentSchema,
    JotformBeforeAttemptDocumentSchema,
]);
export const JotformExternalSubmissionClaimSchema = z
    .object({
    schemaVersion: z.literal(4),
    businessId: BusinessIdSchema,
    integrationId: JotformIntegrationIdSchema,
    checkoutId: CheckoutIdSchema,
    submissionId: JotformSubmissionIdSchema,
    environmentKey: JotformEnvironmentKeySchema,
    region: JotformRegionSchema,
    formId: JotformFormIdSchema,
    providerAccountId: JotformProviderAccountIdSchema,
    paymentTiming: JotformPaymentTimingSchema,
    createdAt: JotformTimestampSchema,
})
    .strict();
